import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Clock, X, Crown, Medal } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Monthly markets contest — countdown strip + winners celebration.
 * - Displays a live HH:MM:SS countdown (days/hours/min/sec) above the home
 *   sections; backend defines start + duration_days.
 * - Polls /api/contest/state every 60s so the live top-3 stays fresh while
 *   the contest is active, and re-fetches once when the timer hits zero.
 * - When `is_active` flips to false, opens a celebratory modal with the
 *   locked top-3 (gold/silver/bronze pedestal) for 10 seconds. Customer
 *   can close it via the ✕ button. We remember the last-seen `end_at` in
 *   localStorage so the modal doesn't replay on every page navigation.
 */
const ContestStrip = () => {
  const token = localStorage.getItem("jumlaty_token");
  const [state, setState] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [resetting, setResetting] = useState(false);
  const lastEndKey = useRef("jumlaty_contest_celebrated_end");

  const fetchState = async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/contest/state`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setState(data);
      setRemaining(Math.max(0, Number(data.remaining_seconds || 0)));
      if (!data.is_active && data.top_3?.length) {
        const lastSeen = localStorage.getItem(lastEndKey.current);
        if (lastSeen !== data.end_at) {
          setShowCelebration(true);
          localStorage.setItem(lastEndKey.current, data.end_at);
          setTimeout(() => setShowCelebration(false), 10000);
        }
      }
    } catch { /* silent */ }
  };

  // Detect admin role for the manual reset banner
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setIsAdmin(r?.data?.role === "admin"))
      .catch(() => {});
  }, [token]);

  // Initial fetch + 60s background refresh
  useEffect(() => {
    fetchState();
    const id = setInterval(fetchState, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!state?.is_active) return;
    const id = setInterval(() => {
      setRemaining((r) => {
        const next = Math.max(0, r - 1);
        if (next === 0) fetchState();
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.is_active]);

  const handleAdminReset = async () => {
    if (!token) return;
    // Ask the admin how many days the fresh cycle should last. Default 30.
    // Anything 1–365 is accepted by the backend. Empty / cancel = abort.
    const raw = window.prompt(
      "🏁 بدء سباق جديد\n\nكم يوم يمتد السباق الجديد؟\n(اكتب رقم بين 1 و 365 — الافتراضي 30)",
      "30"
    );
    if (raw == null) return; // cancelled
    const days = parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(days) || days < 1 || days > 365) {
      window.alert("⚠️ الرقم غير صحيح. اكتب عدد أيام بين 1 و 365.");
      return;
    }
    setResetting(true);
    try {
      await axios.post(`${API}/admin/contest/reset?duration_days=${days}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchState();
    } finally {
      setResetting(false);
    }
  };

  // Admin can also start a fresh race MID-cycle (e.g. to switch from a
  // 30-day to a 60-day sprint without waiting for the current one to end).
  // Confirms first so it isn't triggered by accident, then reuses the same
  // handler which prompts for days.
  const handleAdminStartFresh = async () => {
    if (!token) return;
    if (!window.confirm("سيتم إنهاء السباق الحالي وبدء سباق جديد بعدد أيام تحدده. متأكد؟")) return;
    await handleAdminReset();
  };

  if (!state) return null;

  const days = Math.floor(remaining / 86400);
  const hours = Math.floor((remaining % 86400) / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  const seconds = remaining % 60;
  const pad = (n) => String(n).padStart(2, "0");

  return (
    <>
      <div
        data-testid="contest-strip"
        className="bg-gradient-to-l from-[#7e22ce] via-[#5b21b6] to-[#1e1b4b] text-white rounded-2xl p-4 shadow-xl border border-white/10 relative overflow-hidden"
      >
        {/* Decorative glow */}
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-amber-300/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-pink-400/20 rounded-full blur-3xl pointer-events-none" />

        <div className="relative flex items-center justify-between mb-3 gap-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-amber-400 flex items-center justify-center shadow-lg">
              <Trophy className="w-5 h-5 text-amber-900" strokeWidth={2.6} />
            </div>
            <div>
              <h3 className="text-base font-extrabold leading-tight">سباق الماركتات الشهري</h3>
              <p className="text-[11px] opacity-80">الأكثر طلباً يفوز بالمركز الأول!</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Admin-only: manually reset the contest with a custom
                duration (in days). Useful when the admin wants to run a
                60-day sprint, or to end a cycle early without waiting
                for the timer to hit zero. */}
            {isAdmin && state.is_active && (
              <button
                onClick={handleAdminStartFresh}
                disabled={resetting}
                data-testid="contest-admin-start-fresh"
                title="إنهاء الحالي وبدء سباق جديد بعدد أيام مخصص"
                className="bg-amber-400/20 hover:bg-amber-400/30 disabled:opacity-60 text-amber-100 font-bold text-[11px] px-2.5 py-1 rounded-full border border-amber-300/40 whitespace-nowrap"
              >
                🏁 سباق جديد
              </button>
            )}
            <Clock className="w-5 h-5 opacity-70" />
          </div>
        </div>

        <div className="relative grid grid-cols-4 gap-2" dir="ltr" data-testid="contest-countdown">
          <CountCell label="يوم" value={pad(days)} />
          <CountCell label="ساعة" value={pad(hours)} />
          <CountCell label="دقيقة" value={pad(minutes)} />
          <CountCell label="ثانية" value={pad(seconds)} />
        </div>

        {state.is_active && state.top_3?.length > 0 && (
          <div className="relative mt-3 pt-3 border-t border-white/10">
            <div className="text-[11px] opacity-80 mb-1.5">📊 المتصدّرون الآن:</div>
            <div className="space-y-1">
              {state.top_3.slice(0, 3).map((m, i) => (
                <div key={m.user_id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-base">{i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉"}</span>
                    <span className="truncate font-bold">{m.market_name}</span>
                  </div>
                  <span className="text-amber-300 font-extrabold tabular-nums">{m.total_quantity}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Admin approval banner — appears when the timer has hit zero and
            backend flagged `pending_admin_reset=true`. Admin presses
            "موافق" to zero the contest counter (without touching debts or
            rewards) and start a fresh monthly cycle. */}
        {isAdmin && state.pending_admin_reset && (
          <div className="relative mt-3 pt-3 border-t border-amber-300/30 bg-amber-400/10 -mx-4 -mb-4 px-4 pb-4 rounded-b-2xl">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-extrabold text-amber-200">🏁 انتهت المسابقة — موافقة الأدمن مطلوبة</div>
                <p className="text-[11px] opacity-85 mt-0.5">اضغط (موافق) لتصفير العدّاد وبدء سباق جديد. الديون والمكافآت لن تتأثر.</p>
              </div>
              <button
                onClick={handleAdminReset}
                disabled={resetting}
                data-testid="contest-admin-approve-reset"
                className="bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-amber-900 font-extrabold text-sm px-4 py-2 rounded-full shrink-0"
              >
                {resetting ? "جاري التصفير..." : "موافق"}
              </button>
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showCelebration && (
          <CelebrationModal
            winners={state.top_3 || []}
            onClose={() => setShowCelebration(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

const CountCell = ({ label, value }) => (
  <div className="bg-white/15 backdrop-blur-sm rounded-xl py-2 text-center border border-white/10">
    <div className="text-2xl sm:text-3xl font-extrabold tabular-nums leading-none">{value}</div>
    <div className="text-[10px] opacity-80 mt-1">{label}</div>
  </div>
);

const Confetti = () => {
  // 24 fireworks-like sparks bursting from the top center
  const sparks = Array.from({ length: 24 }, (_, i) => i);
  return (
    <div className="pointer-events-none fixed inset-0 z-[200] overflow-hidden">
      {sparks.map((i) => {
        const angle = (i / 24) * Math.PI * 2;
        const dist = 220 + Math.random() * 180;
        const x = Math.cos(angle) * dist;
        const y = Math.sin(angle) * dist;
        const colors = ["#FCD34D", "#F472B6", "#34D399", "#60A5FA", "#A78BFA", "#FB923C"];
        const c = colors[i % colors.length];
        return (
          <motion.span
            key={i}
            initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
            animate={{ x, y, opacity: 0, scale: 0.4 }}
            transition={{ duration: 1.6, delay: (i % 6) * 0.08, ease: "easeOut" }}
            className="absolute top-1/3 left-1/2 w-3 h-3 rounded-full"
            style={{ backgroundColor: c, boxShadow: `0 0 12px ${c}` }}
          />
        );
      })}
    </div>
  );
};

const CelebrationModal = ({ winners, onClose }) => {
  const [g, s, b] = [winners[0], winners[1], winners[2]];
  return (
    <>
      <Confetti />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[210] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.85, y: 30 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.85, y: 30 }}
          transition={{ type: "spring", stiffness: 250, damping: 22 }}
          className="bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#7e22ce] text-white rounded-3xl max-w-md w-full p-6 relative shadow-2xl border border-amber-300/30"
          onClick={(e) => e.stopPropagation()}
          data-testid="contest-celebration-modal"
        >
          <button
            onClick={onClose}
            data-testid="celebration-close"
            className="absolute top-3 left-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="text-center mb-5">
            <div className="text-5xl mb-2">🏆</div>
            <h2 className="text-2xl font-extrabold">انتهى السباق الشهري!</h2>
            <p className="text-sm opacity-80 mt-1">مبروك للماركتات الفائزة الثلاث 🎉</p>
          </div>

          <div className="space-y-3">
            {g && <WinnerRow rank={1} m={g} icon={<Crown className="w-5 h-5" />} color="bg-amber-400 text-amber-900" />}
            {s && <WinnerRow rank={2} m={s} icon={<Medal className="w-5 h-5" />} color="bg-slate-300 text-slate-800" />}
            {b && <WinnerRow rank={3} m={b} icon={<Medal className="w-5 h-5" />} color="bg-orange-400 text-orange-900" />}
            {winners.length === 0 && (
              <div className="text-center opacity-80 py-6">لم يُسجَّل أي فائز بهذه الجولة.</div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </>
  );
};

const WinnerRow = ({ rank, m, icon, color }) => {
  // Stagger entrance and add a subtle pulse to make 1st-place feel bigger
  // than 2nd/3rd without rebuilding the whole celebration shell.
  const isFirst = rank === 1;
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: rank * 0.25, type: "spring", stiffness: 220, damping: 20 }}
      className={`flex items-center gap-3 rounded-2xl border ${
        isFirst
          ? "bg-gradient-to-l from-amber-400/30 to-amber-200/20 border-amber-300/60 p-4 shadow-lg ring-2 ring-amber-300/40"
          : "bg-white/10 border-white/10 p-3"
      }`}
    >
      <motion.div
        animate={isFirst ? { scale: [1, 1.12, 1] } : { scale: 1 }}
        transition={isFirst ? { duration: 1.4, repeat: Infinity } : {}}
        className={`rounded-full flex items-center justify-center font-extrabold ${color} ${
          isFirst ? "w-14 h-14" : "w-10 h-10"
        }`}
      >
        {icon}
      </motion.div>
      <div className="flex-1 min-w-0">
        <div className={`opacity-75 ${isFirst ? "text-sm" : "text-[11px]"}`}>المركز {rank}</div>
        <div className={`font-extrabold truncate ${isFirst ? "text-xl" : "text-base"}`}>{m.market_name}</div>
      </div>
      <div className={`text-amber-300 font-extrabold tabular-nums ${isFirst ? "text-2xl" : "text-base"}`}>
        {m.total_quantity}
      </div>
    </motion.div>
  );
};

export default ContestStrip;
